import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { formatEventTimeRange } from '../ManageEvent/eventTimeUtils';
import { isOnlineEventFormat } from '../ManageEvent/eventFormatConstants';
import OnlineEventLocationDisplay from './OnlineEventLocationDisplay';

const QuickInfoRoot = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(2.5, 0, 2),
}));

const InfoColumn = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1.5, 0),
  },
}));

const LabelRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  marginBottom: theme.spacing(1.25),
}));

const iconSx = {
  fontSize: 22,
  color: 'text.secondary',
  flexShrink: 0,
};

const labelSx = {
  fontSize: '0.875rem',
  color: 'text.secondary',
  fontWeight: 400,
  lineHeight: 1.2,
};

const primarySx = {
  fontSize: '0.875rem',
  fontWeight: 700,
  color: 'text.primary',
  lineHeight: 1.4,
  display: 'block',
};

const secondarySx = {
  fontSize: '0.875rem',
  fontWeight: 400,
  color: 'text.primary',
  lineHeight: 1.4,
  display: 'block',
  mt: 0.25,
};

const ValueStack = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(4.25),
}));

const InfoItem = ({ icon, label, primary, secondary, children }) => (
  <InfoColumn>
    <LabelRow>
      {icon}
      <Typography sx={labelSx}>{label}</Typography>
    </LabelRow>
    <ValueStack>
      {children ?? (
        <>
          <Typography sx={primarySx}>{primary}</Typography>
          {secondary ? <Typography sx={secondarySx}>{secondary}</Typography> : null}
        </>
      )}
    </ValueStack>
  </InfoColumn>
);

const EventDetailsQuickInfo = ({ event }) => {
  const seatsTotal = Number(event.totalSeats ?? 0);
  const seatsAvailable = Number(event.availableSeats ?? 0);
  const seatsDisplay = seatsTotal > 0 ? `${seatsAvailable}/${seatsTotal}` : '—';
  const timeDisplay =
    formatEventTimeRange(event.startTime, event.endTime) || event.timeRange || null;
  const isOnline = isOnlineEventFormat(event.eventFormat);
  const meetingLink = event.onlineMeetingLink ?? '';

  return (
    <QuickInfoRoot container>
      <Grid item xs={12} md={4}>
        <InfoItem
          icon={<CalendarTodayOutlinedIcon sx={iconSx} />}
          label="Date & Time"
          primary={event.date || '—'}
          secondary={timeDisplay}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <InfoItem icon={<PlaceOutlinedIcon sx={iconSx} />} label="Location">
          {isOnline ? (
            <OnlineEventLocationDisplay meetingLink={meetingLink} />
          ) : (
            <>
              <Typography sx={primarySx}>{event.venueName || '—'}</Typography>
              {event.address ? <Typography sx={secondarySx}>{event.address}</Typography> : null}
            </>
          )}
        </InfoItem>
      </Grid>
      <Grid item xs={12} md={4}>
        <InfoItem
          icon={<GroupsOutlinedIcon sx={iconSx} />}
          label="Seats Remaining"
          primary={seatsDisplay}
          secondary={event.seatsStatusLabel}
        />
      </Grid>
    </QuickInfoRoot>
  );
};

export default EventDetailsQuickInfo;
